/**
 * Archive node inspectors — one file for the five nodes that ask "which
 * archive?" first and differ in the second question.
 *
 * - `archive.sink`             — Archive to container: archive + retention + the
 *                                metadata mapping.
 * - `archive.dataset_sink`     — Archive dataset: archive + table name.
 * - `archive.ensure_container` — Ensure archive: provision-or-reuse, with the
 *                                metadata model.
 * - `archive.source`           — Read from archive: catalogue or rows.
 * - `archive.disposition` / `archive.verify` — Disposition review and checksum
 *                                verification, one panel.
 *
 * The mapping is the interesting part — it is what turns a source field into a
 * metadata field and then into a search facet — so it is editable here rather
 * than only in a seed script.
 */

import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  listArchiveContainers,
  listArchiveDatasets,
  listRecordViews,
  type ArchiveContainer,
} from "@/shared/api/archive";
import { toastApiError } from "@/shared/ui/toast";

type Option = { value: string; label: string };

/** Archives the running actor may write into, filtered to the shape the node
 *  can actually handle: documents for `archive.sink`, tables for
 *  `archive.dataset_sink`. Offering the wrong one only produces a run-time
 *  failure further away from the mistake that caused it. */
function useArchiveContainers(kind?: "documents" | "datasets") {
  const getToken = useAccessToken();
  const [items, setItems] = useState<Option[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const all = (await listArchiveContainers(token)).items;
        if (cancelled) return;
        setItems(
          all
            .filter((c) => !kind || c.kind === kind)
            .map((c) => ({ value: c.container_id, label: `${c.name} (${c.code})` })),
        );
      } catch (err) {
        toastApiError("Could not load archives", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, kind]);
  return items;
}

const RETENTION_CLASSES = ["5y", "10y", "15y", "30y", "permanent"];

type MetadataMapping = { source: string; target: string };

export function ArchiveSinkConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  // Keyed by port, as everywhere else on this canvas. `archive.sink` has one
  // input, so `in-0` is the upstream document source's columns.
  inputColumns: Record<string, string[]>;
}) {
  const containers = useArchiveContainers("documents");
  const sourceColumns = inputColumns["in-0"] ?? [];
  const mappings: MetadataMapping[] = Array.isArray(config.mappings)
    ? (config.mappings as MetadataMapping[])
    : [];

  const update = (i: number, patch: Partial<MetadataMapping>) => {
    const next = mappings.map((m, j) => (j === i ? { ...m, ...patch } : m));
    onChange("mappings", next);
  };

  return (
    <Stack gap="xs">
      <Select
        label="Archive into"
        description="Empty takes the archive an upstream Ensure archive node provisions"
        placeholder={containers.length ? "Select an archive" : "No document archives"}
        data={containers}
        value={String(config.containerId ?? "") || null}
        disabled={!editable || !containers.length}
        searchable
        clearable
        onChange={(v) => {
          onChange("containerId", v ?? "");
          // Stored beside the id so the node card can say which archive it
          // writes to without a lookup — the same pattern connections use.
          onChange("containerName", containers.find((c) => c.value === v)?.label ?? "");
        }}
      />

      <Select
        label="Retention for this intake"
        description="Empty uses the archive's own class"
        data={RETENTION_CLASSES}
        value={String(config.retentionClass ?? "") || null}
        disabled={!editable}
        clearable
        onChange={(v) => onChange("retentionClass", v ?? "")}
      />

      <Select
        label="If a record was already archived"
        data={[
          { value: "skip", label: "Skip it — do not archive twice" },
          { value: "version", label: "Archive as a new version" },
        ]}
        value={String(config.onDuplicate ?? "skip")}
        disabled={!editable}
        onChange={(v) => onChange("onDuplicate", v ?? "skip")}
      />

      <Text size="xs" c="dimmed">
        Documents are downloaded from the vault the upstream Veeva Source read — the query must
        select <Text span ff="monospace">id</Text> (and read <Text span ff="monospace">FROM documents</Text>).
      </Text>

      <Divider
        my={4}
        label="Metadata mapping"
        labelPosition="left"
        styles={{ label: { fontSize: 11, fontWeight: 600 } }}
      />
      <Text size="xs" c="dimmed">
        Each mapping copies a source field onto the archive&apos;s metadata model. A target the
        model does not carry yet is created, and becomes a search facet.
      </Text>

      {mappings.map((m, i) => (
        <Group key={i} gap={6} wrap="nowrap" align="flex-end">
          {sourceColumns.length ? (
            <Select
              flex={1}
              size="xs"
              label={i === 0 ? "From" : undefined}
              placeholder="source field"
              data={sourceColumns}
              value={m.source || null}
              disabled={!editable}
              searchable
              onChange={(v) => update(i, { source: v ?? "" })}
            />
          ) : (
            <TextInput
              flex={1}
              size="xs"
              label={i === 0 ? "From" : undefined}
              placeholder="source field"
              value={m.source ?? ""}
              disabled={!editable}
              onChange={(e) => update(i, { source: e.currentTarget.value })}
            />
          )}
          <TextInput
            flex={1}
            size="xs"
            label={i === 0 ? "To" : undefined}
            placeholder="metadata key"
            value={m.target ?? ""}
            disabled={!editable}
            onChange={(e) => update(i, { target: e.currentTarget.value })}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            disabled={!editable}
            aria-label="Remove mapping"
            onClick={() =>
              onChange(
                "mappings",
                mappings.filter((_, j) => j !== i),
              )
            }
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}

      <Button
        size="compact-xs"
        variant="light"
        disabled={!editable}
        onClick={() => onChange("mappings", [...mappings, { source: "", target: "" }])}
      >
        Add a mapping
      </Button>
    </Stack>
  );
}

export function ArchiveDatasetSinkConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const containers = useArchiveContainers("datasets");
  return (
    <Stack gap="xs">
      <Select
        label="Archive into"
        placeholder={containers.length ? "Select an archive" : "No table archives"}
        data={containers}
        value={String(config.containerId ?? "") || null}
        disabled={!editable || !containers.length}
        searchable
        onChange={(v) => {
          onChange("containerId", v ?? "");
          onChange("containerName", containers.find((c) => c.value === v)?.label ?? "");
        }}
      />
      <TextInput
        label="Table name"
        description="What this table is called inside the archive"
        placeholder="e.g. cases"
        value={String(config.datasetName ?? "")}
        disabled={!editable}
        onChange={(e) => onChange("datasetName", e.currentTarget.value)}
      />
      <Textarea
        label="Description"
        description="Read years from now by someone who never saw the source system"
        autosize
        minRows={2}
        value={String(config.description ?? "")}
        disabled={!editable}
        onChange={(e) => onChange("description", e.currentTarget.value)}
      />
    </Stack>
  );
}

type ModelField = { key: string; label: string; type: string };

export function ArchiveEnsureContainerConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const fields: ModelField[] = Array.isArray(config.fields) ? (config.fields as ModelField[]) : [];

  const update = (i: number, patch: Partial<ModelField>) =>
    onChange(
      "fields",
      fields.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    );

  return (
    <Stack gap="xs">
      <Alert variant="light" color="brand" p="xs">
        <Text size="xs">
          Creates this archive on the first run and reuses it afterwards, so a scheduled pipeline
          does not make a second one. Creating an archive is an administrator action — a Developer
          running this pipeline is refused.
        </Text>
      </Alert>

      <Group grow>
        <TextInput
          label="Code"
          description="Stable handle"
          placeholder="e.g. VQR"
          value={String(config.code ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("code", e.currentTarget.value.toUpperCase())}
        />
        <Select
          label="Holds"
          data={[
            { value: "documents", label: "Documents" },
            { value: "datasets", label: "Retired-system tables" },
          ]}
          value={String(config.kind ?? "documents")}
          disabled={!editable}
          onChange={(v) => onChange("kind", v ?? "documents")}
        />
      </Group>

      <TextInput
        label="Name"
        value={String(config.name ?? "")}
        disabled={!editable}
        onChange={(e) => onChange("name", e.currentTarget.value)}
      />

      <Group grow>
        <TextInput
          label="Source system"
          value={String(config.sourceSystem ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("sourceSystem", e.currentTarget.value)}
        />
        <Select
          label="Cluster"
          data={["A", "B", "C"]}
          value={String(config.cluster ?? "C")}
          disabled={!editable}
          onChange={(v) => onChange("cluster", v ?? "C")}
        />
      </Group>

      <Group grow>
        <Select
          label="Retention"
          data={RETENTION_CLASSES}
          value={String(config.retentionClass ?? "10y")}
          disabled={!editable}
          onChange={(v) => onChange("retentionClass", v ?? "10y")}
        />
        <Select
          label="Object Lock"
          description="Only S3 enforces it"
          data={[
            { value: "", label: "Not applied" },
            { value: "GOVERNANCE", label: "Governance" },
            { value: "COMPLIANCE", label: "Compliance" },
          ]}
          value={String(config.objectLockMode ?? "")}
          disabled={!editable}
          onChange={(v) => onChange("objectLockMode", v ?? "")}
        />
      </Group>

      <Select
        label="If it already exists"
        data={[
          { value: "use", label: "Use it — idempotent" },
          { value: "fail", label: "Fail the run" },
        ]}
        value={String(config.onExisting ?? "use")}
        disabled={!editable}
        onChange={(v) => onChange("onExisting", v ?? "use")}
      />

      <Divider
        my={4}
        label="Metadata model"
        labelPosition="left"
        styles={{ label: { fontSize: 11, fontWeight: 600 } }}
      />
      <Text size="xs" c="dimmed">
        The fields records carry, and the facets end users filter on.
      </Text>

      {fields.map((f, i) => (
        <Group key={i} gap={6} wrap="nowrap" align="flex-end">
          <TextInput
            flex={1}
            size="xs"
            label={i === 0 ? "Key" : undefined}
            value={f.key ?? ""}
            disabled={!editable}
            onChange={(e) => update(i, { key: e.currentTarget.value })}
          />
          <TextInput
            flex={1}
            size="xs"
            label={i === 0 ? "Label" : undefined}
            value={f.label ?? ""}
            disabled={!editable}
            onChange={(e) => update(i, { label: e.currentTarget.value })}
          />
          <Select
            w={92}
            size="xs"
            label={i === 0 ? "Type" : undefined}
            data={["text", "date", "number"]}
            value={f.type ?? "text"}
            disabled={!editable}
            onChange={(v) => update(i, { type: v ?? "text" })}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            disabled={!editable}
            aria-label="Remove field"
            onClick={() =>
              onChange(
                "fields",
                fields.filter((_, j) => j !== i),
              )
            }
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}

      <Button
        size="compact-xs"
        variant="light"
        disabled={!editable}
        onClick={() => onChange("fields", [...fields, { key: "", label: "", type: "text" }])}
      >
        Add a field
      </Button>
    </Stack>
  );
}

/**
 * Read from archive.
 *
 * The container picker is not filtered by kind here — both shapes can be read,
 * they just yield different things: a document archive gives its catalogue, a
 * retired system gives rows. The panel changes shape once an archive is chosen
 * so the difference is visible rather than needing to be known.
 */
export function ArchiveSourceConfig({
  config,
  editable,
  onChange,
  onColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns: (cols: string[]) => void;
}) {
  const getToken = useAccessToken();
  const containerId = String(config.containerId ?? "");
  const [containers, setContainers] = useState<ArchiveContainer[]>([]);
  const [datasets, setDatasets] = useState<Option[]>([]);
  const [views, setViews] = useState<Option[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listArchiveContainers(await getToken())).items;
        if (!cancelled) setContainers(items);
      } catch (err) {
        toastApiError("Could not load archives", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const chosen = containers.find((c) => c.container_id === containerId);
  const isTables = chosen?.kind === "datasets";

  useEffect(() => {
    if (!containerId || !isTables) {
      setDatasets([]);
      setViews([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const [ds, vs] = await Promise.all([
          listArchiveDatasets(token, containerId),
          listRecordViews(token, containerId),
        ]);
        if (cancelled) return;
        setDatasets(ds.items.map((d) => ({ value: d.dataset_name, label: d.dataset_name })));
        setViews(vs.items.map((v) => ({ value: v.view_id, label: v.name })));
      } catch {
        setDatasets([]);
        setViews([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [containerId, isTables, getToken]);

  return (
    <Stack gap="xs">
      <Select
        label="Read from"
        placeholder={containers.length ? "Select an archive" : "No archives you can read"}
        data={containers.map((c) => ({
          value: c.container_id,
          label: `${c.name} (${c.code})`,
        }))}
        value={containerId || null}
        disabled={!editable || !containers.length}
        searchable
        onChange={(v) => {
          onChange("containerId", v ?? "");
          onChange("containerName", containers.find((c) => c.container_id === v)?.name ?? "");
          onChange("datasetName", "");
          onChange("recordViewId", "");
          onColumns([]);
        }}
      />

      {chosen && !isTables && (
        <Alert variant="light" color="brand" p="xs">
          <Text size="xs">
            A document archive yields its <strong>catalogue</strong> — one row per archived record,
            with metadata, checksum and retention state. The files themselves are not read out
            through a pipeline.
          </Text>
        </Alert>
      )}

      {isTables && (
        <>
          <Select
            label="Record view"
            description="A join somebody already worked out — usually the better source"
            placeholder={views.length ? "Optional" : "None saved"}
            data={views}
            value={String(config.recordViewId ?? "") || null}
            disabled={!editable || !views.length}
            clearable
            onChange={(v) => {
              onChange("recordViewId", v ?? "");
              if (v) onChange("datasetName", "");
            }}
          />
          <Select
            label="or a single table"
            placeholder={datasets.length ? "Select a table" : "None"}
            data={datasets}
            value={String(config.datasetName ?? "") || null}
            disabled={!editable || !datasets.length || !!config.recordViewId}
            searchable
            clearable
            onChange={(v) => onChange("datasetName", v ?? "")}
          />
        </>
      )}

      <NumberInput
        label="Row limit"
        description="Guards a report against pulling an entire archive by accident"
        min={1}
        max={50000}
        value={Number(config.limit ?? 5000)}
        disabled={!editable}
        onChange={(v) => onChange("limit", typeof v === "number" ? v : 5000)}
      />

      <Text size="xs" c="dimmed">
        Runs as whoever starts the pipeline: reading an archive needs the same retrieve grant it
        needs in the UI, so orchestration is not a way around access.
      </Text>
    </Stack>
  );
}

/**
 * Disposition review and checksum verification.
 *
 * One panel for both: they ask the same first question — which archive — and
 * differ only in the second. Splitting them would duplicate the picker and the
 * loading state to save a single conditional.
 */
export function ArchiveReviewConfig({
  kind,
  config,
  editable,
  onChange,
}: {
  kind: string;
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const containers = useArchiveContainers("documents");
  const isVerify = kind === "archive.verify";

  return (
    <Stack gap="xs">
      <Select
        label="Archive"
        placeholder={containers.length ? "Select an archive" : "No archives you can read"}
        data={containers}
        value={String(config.containerId ?? "") || null}
        disabled={!editable || !containers.length}
        searchable
        onChange={(v) => {
          onChange("containerId", v ?? "");
          onChange("containerName", containers.find((c) => c.value === v)?.label ?? "");
        }}
      />

      {isVerify ? (
        <>
          <NumberInput
            label="Sample size"
            description="0 checks every object — thorough, and slow on a large archive"
            min={0}
            max={5000}
            value={Number(config.sampleSize ?? 100)}
            disabled={!editable}
            onChange={(v) => onChange("sampleSize", typeof v === "number" ? v : 0)}
          />
          <Alert variant="light" color="brand" p="xs">
            <Text size="xs">
              Objects are re-read from storage and their checksums recomputed. A monthly sample that
              actually runs is worth more than an exhaustive check somebody switched off — the
              result reports which of the two it was.
            </Text>
          </Alert>
        </>
      ) : (
        <>
          <NumberInput
            label="Look ahead (days)"
            description="0 is what is due today. 30 answers what falls due before the next monthly run."
            min={0}
            max={3650}
            value={Number(config.withinDays ?? 0)}
            disabled={!editable}
            onChange={(v) => onChange("withinDays", typeof v === "number" ? v : 0)}
          />
          <Alert variant="light" color="brand" p="xs">
            <Text size="xs">
              <strong>Reports only — nothing is deleted.</strong> Records under legal hold are
              listed separately, because &quot;held, not reviewed&quot; is a different answer from
              &quot;not due&quot; and an inspector asks about both.
            </Text>
          </Alert>
        </>
      )}
    </Stack>
  );
}
